import { IField } from "@/api/AppApi/services/builder/interfaces/interfaces";
import { useUploadFile } from "@/api/AppApi/services/upload-file";
import { useEffect, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import FileThumbmail from "./upload-files-helpers/file-thumbmail/FileThumbmail";
import SvgUploadFiles from "./SvgUploadFiles";
import {
  IUploadErr,
  TempFile,
  fileNotAccepted,
  supportedFileExts,
} from "./upload-files-helpers/upload";
import InputErrorMsg from "../../form-builder-helpers/InputErrorMsg";
import ToastForUpload from "./upload-files-helpers/ToastForUpload";
import { useTranslation } from "@/hooks/localization/useTranslations";

interface Props {
  field: IField;
  form: UseFormReturn<FieldValues, any>;
}
const noFile: TempFile = {
  id: 1,
  name: "",
  url: "",
  isImage: false,
};
const HookUploadOneFile: React.FC<Props> = ({ form, field }) => {
  const t = useTranslation();
  const fConfig = field.configurations;
  const initState = {
    id: 1,
    name: field?.value ?? "",
    url: field?.value ?? "",
    isImage: getIsImage(field?.value),
  };
  const [uploadedFile, setUploadedFile] = useState<TempFile>(noFile);
  const [msgErr, setMsgErr] = useState<IUploadErr[]>([]);
  const uploadFile = useUploadFile({
    onSuccess: (res: any) => {
      setUploadedFile((prev) => ({
        ...prev,
        url: res.result.absolute_url ?? null,
        isImage: res.result?.mime?.includes("image"),
      }));
    },
    onError: (err: any) => {
      if (err.response?.status === 422) {
        setUploadedFile(initState);
      } else {
        // console.log(err);
      }
    },
  });
  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList) return;
    const file = fileList[0];
    const illegalFile = fileNotAccepted(file);
    if (illegalFile) {
      setMsgErr([illegalFile]);
      return;
    }
    setUploadedFile((prev) => ({
      ...prev,
      name: file.name,
      url: "",
      isImage: false,
    }));
    uploadFile.mutate({ file: file });
  };
  useEffect(() => {
    setUploadedFile(initState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field?.value]);

  useEffect(() => {
    setTimeout(() => form.setValue(fConfig.key, uploadedFile.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFile]);

  const handleRemove = () => {
    setUploadedFile(noFile);
  };
  return (
    <div>
      {msgErr.length > 0 && (
        <ToastForUpload
          message={msgErr}
          show={msgErr.length > 0}
          setShow={() => setMsgErr([])}
        />
      )}
      {uploadedFile.name && (
        <FileThumbmail
          file={uploadedFile}
          onRemoveClick={() => handleRemove()}
        />
      )}
      <label className="upload-btn">
        <input
          type="file"
          multiple={false}
          onClick={(e) => {
            (e.target as HTMLInputElement).value = "";
          }}
          accept={supportedFileExts.join(",")}
          {...form.register(fConfig.key, {
            onChange: (e) => handleUpload((e.target as HTMLInputElement).files),
            required:
              fConfig.is_required && !uploadedFile.url
                ? t.this_field_is_required
                : "",
          })}
        />
        <div className=" d-flex align-items-center gap-1 justify-content-center">
          <SvgUploadFiles />
          <p style={{ maxWidth: 300 }} className="m-0 category-label-advance">
            {fConfig.placeholder}
          </p>
        </div>
      </label>
      {fConfig.hint && <div className="field-hint">{fConfig.hint}</div>}
      {form.formState.errors[fConfig.key] && (
        <InputErrorMsg
          errorMsg={form.formState.errors[fConfig.key]?.message as string}
        />
      )}
    </div>
  );
};

export default HookUploadOneFile;

export const getIsImage = (fileURL: string) => {
  if (typeof fileURL !== "string") return false;
  const arr = fileURL?.split(".");
  const ext = arr[arr.length - 1];
  const imagesExt = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "tiff",
    "tif",
    "webp",
    "svg",
    "ico",
    "heif",
    "heic",
  ];
  if (imagesExt.includes(ext.toLocaleLowerCase())) return true;
  return false;
};
